import React, { useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DayDetailsModal } from './DayDetailsModal';
import { ContextMenu } from './ContextMenu';
import type { CalendarDay, ScheduleType } from '@/types/schedule.types';

interface CalendarMonthProps {
  year: number;
  month: number;
  calendarDays: CalendarDay[];
  onNavigate: (year: number, month: number) => void;
  onDayClick?: (day: CalendarDay) => void;
  onDayUpdate?: (dayId: string, updates: Partial<CalendarDay>) => void;
  onBulkUpdate?: (dayIds: string[], updates: Partial<CalendarDay>) => void;
  templateName?: string;
  isGenerated?: boolean;
  selectedDays?: string[];
  onDaySelect?: (dayIds: string[]) => void;
  onBulkEdit?: () => void;
}

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const WEEKDAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const getDayColorClass = (scheduleType: string) => {
  switch (scheduleType) {
    case 'working':
      return 'bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-100';
    case 'night_shift':
      return 'bg-indigo-100 text-indigo-900 dark:bg-indigo-900/20 dark:text-indigo-100';
    case 'day_off':
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
    case 'holiday':
      return 'bg-red-100 text-red-900 dark:bg-red-900/20 dark:text-red-100';
    case 'reduced':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100';
    default:
      return 'bg-gray-50 text-gray-400 dark:bg-gray-900/50 dark:text-gray-500';
  }
};

const formatCalendarTime = (time: string | null | undefined) => {
  if (!time) return '';
  const value = time.toString();
  const timePart = value.includes('T') ? value.split('T')[1] : value;
  return timePart.slice(0, 5); // HH:mm
};

export const CalendarMonth: React.FC<CalendarMonthProps> = ({
  year,
  month,
  calendarDays,
  onNavigate,
  onDayClick,
  onDayUpdate,
  onBulkUpdate,
  templateName,
  isGenerated = false,
  selectedDays = [],
  onDaySelect,
  onBulkEdit,
}) => {
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const _dragRef = useRef<HTMLDivElement>(null);

  const handleDayClick = useCallback((day: CalendarDay, event: React.MouseEvent) => {
    event.preventDefault();

    if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd click - toggle selection
      const newSelection = selectedDays.includes(day.id)
        ? selectedDays.filter(id => id !== day.id)
        : [...selectedDays, day.id];
      onDaySelect?.(newSelection);
    } else if (event.shiftKey && selectedDays.length > 0) {
      // Shift click - select range
      const lastSelectedDay = calendarDays.find(d => d.id === selectedDays[selectedDays.length - 1]);
      if (lastSelectedDay) {
        const startDate = new Date(Math.min(new Date(day.date).getTime(), new Date(lastSelectedDay.date).getTime()));
        const endDate = new Date(Math.max(new Date(day.date).getTime(), new Date(lastSelectedDay.date).getTime()));

        const rangeSelection: string[] = [];
        for (const calDay of calendarDays) {
          const dayDate = new Date(calDay.date);
          if (dayDate >= startDate && dayDate <= endDate) {
            rangeSelection.push(calDay.id);
          }
        }
        onDaySelect?.(rangeSelection);
      }
    } else {
      // Regular click
      onDayClick?.(day);
      setSelectedDay(day);
      setIsModalOpen(true);
    }
  }, [selectedDays, calendarDays, onDayClick, onDaySelect]);

  const handleDoubleClick = useCallback((day: CalendarDay, event: React.MouseEvent) => {
    event.preventDefault();
    setSelectedDay(day);
    setIsModalOpen(true);
  }, []);

  const handleMouseDown = useCallback((day: CalendarDay, event: React.MouseEvent) => {
    if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
      setIsDragging(true);
      setDragStart(day.id);
      onDaySelect?.([day.id]);
    }
  }, [onDaySelect]);

  const handleMouseEnter = useCallback((day: CalendarDay) => {
    if (isDragging && dragStart) {
      const startDay = calendarDays.find(d => d.id === dragStart);
      if (startDay) {
        const startDate = new Date(Math.min(new Date(day.date).getTime(), new Date(startDay.date).getTime()));
        const endDate = new Date(Math.max(new Date(day.date).getTime(), new Date(startDay.date).getTime()));

        const rangeSelection: string[] = [];
        for (const calDay of calendarDays) {
          const dayDate = new Date(calDay.date);
          if (dayDate >= startDate && dayDate <= endDate) {
            rangeSelection.push(calDay.id);
          }
        }
        onDaySelect?.(rangeSelection);
      }
    }
  }, [isDragging, dragStart, calendarDays, onDaySelect]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  React.useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
      setDragStart(null);
    };

    if (isDragging) {
      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [isDragging]);

  const handleDayUpdate = useCallback((dayId: string, updates: Partial<CalendarDay>) => {
    onDayUpdate?.(dayId, updates);
    setIsModalOpen(false);
  }, [onDayUpdate]);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    if (selectedDays.length > 0) {
      event.preventDefault();
      setContextMenu({ x: event.clientX, y: event.clientY });
    }
  }, [selectedDays]);

  const handleBulkSetScheduleType = useCallback((type: ScheduleType) => {
    if (onBulkUpdate && selectedDays.length > 0) {
      onBulkUpdate(selectedDays, {
        schedule_type: type,
        is_working_day: type === 'working' || type === 'reduced' || type === 'night_shift'
      });
    }
  }, [onBulkUpdate, selectedDays]);

  const handleCopySettings = useCallback(() => {
    if (selectedDays.length === 1) {
      const selectedDay = calendarDays.find(day => day.id === selectedDays[0]);
      if (selectedDay) {
        // Store in localStorage for now - could be improved with a proper clipboard service
        localStorage.setItem('scheduleClipboard', JSON.stringify({
          schedule_type: selectedDay.schedule_type,
          shift_start: selectedDay.shift_start,
          shift_end: selectedDay.shift_end,
          is_working_day: selectedDay.is_working_day
        }));
      }
    }
  }, [selectedDays, calendarDays]);

  const handleResetToDefault = useCallback(() => {
    if (onBulkUpdate && selectedDays.length > 0) {
      onBulkUpdate(selectedDays, {
        schedule_type: 'working',
        shift_start: '09:00:00',
        shift_end: '18:00:00',
        is_working_day: true
      });
    }
  }, [onBulkUpdate, selectedDays]);
  const goToPrevMonth = () => {
    if (month === 1) {
      onNavigate(year - 1, 12);
    } else {
      onNavigate(year, month - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 12) {
      onNavigate(year + 1, 1);
    } else {
      onNavigate(year, month + 1);
    }
  };

  const firstDayOfMonth = new Date(year, month - 1, 1);
  const lastDayOfMonth = new Date(year, month, 0);
  const startDate = new Date(firstDayOfMonth);
  const dayOfWeek = startDate.getDay() === 0 ? 7 : startDate.getDay();
  startDate.setDate(startDate.getDate() - dayOfWeek + 1);

  const endDate = new Date(lastDayOfMonth);
  const endDayOfWeek = endDate.getDay() === 0 ? 7 : endDate.getDay();
  endDate.setDate(endDate.getDate() + (7 - endDayOfWeek));

  // Normalize dates to YYYY-MM-DD format for consistent matching
  const calendarMap = new Map(
    calendarDays.map(day => {
      const dateKey = day.date.split('T')[0]; // Extract YYYY-MM-DD from ISO string
      return [dateKey, day];
    })
  );

  const weeks: Date[][] = [];
  let currentWeek: Date[] = [];

  for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
    currentWeek.push(new Date(date));

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  return (
    <div className="h-full flex flex-col">
      <div className="border-b p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">
              {MONTH_NAMES[month - 1]} {year}
            </h2>
            {templateName && (
              <p className="text-sm text-muted-foreground mt-1">
                Шаблон: {templateName}
                {isGenerated && (
                  <span className="ml-2 inline-flex items-center">
                    <Info className="h-3 w-3 mr-1" />
                    Сгенерировано
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPrevMonth}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextMonth}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 overflow-auto" onContextMenu={handleContextMenu}>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAY_NAMES.map((day, index) => (
            <div
              key={index}
              className="p-2 text-center text-sm font-medium text-muted-foreground"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 flex-1">
          {weeks.map((week, weekIndex) =>
            week.map((date, dayIndex) => {
              // Формируем ключ даты в локальной таймзоне, чтобы не было сдвига из-за UTC
              const dateString = `${date.getFullYear()}-${String(
                date.getMonth() + 1,
              ).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              const calendarDay = calendarMap.get(dateString);
              const isCurrentMonth = date.getMonth() === month - 1;
              const today = new Date();
              const todayKey = `${today.getFullYear()}-${String(
                today.getMonth() + 1,
              ).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
              const isToday = dateString === todayKey;

              return (
                <button
                  key={`${weekIndex}-${dayIndex}`}
                  onClick={(e) => calendarDay && handleDayClick(calendarDay, e)}
                  onDoubleClick={(e) => calendarDay && handleDoubleClick(calendarDay, e)}
                  onMouseDown={(e) => calendarDay && handleMouseDown(calendarDay, e)}
                  onMouseEnter={() => calendarDay && handleMouseEnter(calendarDay)}
                  onMouseUp={handleMouseUp}
                  disabled={!isCurrentMonth || !calendarDay}
                  className={cn(
                    'aspect-square p-1 rounded-lg transition-colors text-xs border select-none',
                    isCurrentMonth
                      ? calendarDay
                        ? getDayColorClass(calendarDay.schedule_type)
                        : 'bg-gray-50 text-gray-400'
                      : 'bg-transparent text-gray-300 cursor-not-allowed',
                    isToday && isCurrentMonth && 'ring-2 ring-blue-500',
                    calendarDay && selectedDays.includes(calendarDay.id) && 'ring-2 ring-purple-500',
                    calendarDay && (onDayClick || onDaySelect) && 'hover:opacity-80 cursor-pointer'
                  )}
                >
                  <div className="h-full flex flex-col justify-between">
                    <div className="font-medium">{date.getDate()}</div>

                    {calendarDay && isCurrentMonth && (
                      <div className="flex flex-col gap-px">
                        {calendarDay.shift_start && calendarDay.shift_end && (
                          <div className="text-[10px] opacity-90">
                            {formatCalendarTime(calendarDay.shift_start)}-{formatCalendarTime(calendarDay.shift_end)}
                          </div>
                        )}
                        <div className="text-[9px] opacity-75">
                          {calendarDay.schedule_type === 'working' ? 'Раб' :
                           calendarDay.schedule_type === 'night_shift' ? 'Ноч' :
                           calendarDay.schedule_type === 'holiday' ? 'Праз' :
                           calendarDay.schedule_type === 'reduced' ? 'Сокр' : 'Вых'}
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="border-t p-4">
        <div className="flex flex-wrap gap-2 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-100 border" />
            <span>Рабочий день</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-indigo-100 border" />
            <span>Ночная смена</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-gray-100 border" />
            <span>Выходной</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-red-100 border" />
            <span>Праздник</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-100 border" />
            <span>Сокращенный</span>
          </div>
        </div>
      </div>

      <DayDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        day={selectedDay}
        templateName={templateName || 'Неизвестный шаблон'}
        onUpdate={handleDayUpdate}
        isReadOnly={isGenerated}
      />

      <ContextMenu
        isOpen={!!contextMenu}
        position={contextMenu || { x: 0, y: 0 }}
        selectedDays={calendarDays.filter(day => selectedDays.includes(day.id))}
        onClose={() => setContextMenu(null)}
        onBulkEdit={() => {
          onBulkEdit?.();
          setContextMenu(null);
        }}
        onCopy={handleCopySettings}
        onDelete={handleResetToDefault}
        onSetScheduleType={handleBulkSetScheduleType}
      />
    </div>
  );
};